import { useMemo, useState } from "react";
import { trendingList } from "@/mocks/communityData";

const modes = [
  { key: "rank", label: "全网热榜", icon: "ri-fire-line" },
  { key: "rising", label: "飙升最快", icon: "ri-rocket-2-line" },
  { key: "hot", label: "讨论最多", icon: "ri-chat-voice-line" },
] as const;

type ModeKey = (typeof modes)[number]["key"];

const trendIcon: Record<string, { icon: string; cls: string }> = {
  up: { icon: "ri-arrow-up-line", cls: "text-primary-600" },
  down: { icon: "ri-arrow-down-line", cls: "text-secondary-600" },
  new: { icon: "ri-sparkling-2-fill", cls: "text-accent-600" },
};

const toneBg: Record<string, string> = {
  "全网热榜": "bg-primary-100 text-primary-800",
  "吃瓜现场": "bg-accent-100 text-accent-800",
  "灌水热聊": "bg-secondary-100 text-secondary-800",
  "爆料解读": "bg-foreground-100 text-foreground-700",
  "八卦闲聊": "bg-secondary-100 text-secondary-800",
  "现场实况": "bg-accent-100 text-accent-800",
  "话题广场": "bg-primary-100 text-primary-800",
};

export default function Trending() {
  const [mode, setMode] = useState<ModeKey>("rank");

  const sorted = useMemo(() => {
    if (mode === "rising") {
      return [...trendingList].sort((a, b) => (b.trend === "new" ? 1 : 0) - (a.trend === "new" ? 1 : 0));
    }
    if (mode === "hot") {
      return [...trendingList].sort((a, b) => parseInt(b.replies) - parseInt(a.replies));
    }
    return trendingList;
  }, [mode]);

  return (
    <section id="trending" aria-label="今日热榜追踪" className="w-full bg-background-100 py-16 md:py-24">
      <div className="container-x">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="tag-primary"><i className="ri-fire-fill"></i>Trending</span>
            <h2 className="mt-3 text-2xl font-black tracking-tight text-foreground-950 md:text-4xl">
              今日热榜 · 全网大事
              <span className="text-primary-500">一起围观</span>
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
              哪些事件正在全网发酵？跟着榜单节奏，第一时间了解大家在聊什么，点进详情加入讨论。
            </p>
          </div>

          <div className="flex items-center gap-1 self-start rounded-full bg-background-50 p-1 ring-1 ring-background-200 md:self-auto">
            {modes.map((m) => (
              <button
                key={m.key}
                type="button"
                onClick={() => setMode(m.key)}
                className={
                  "flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-semibold transition-colors md:px-4 " +
                  (mode === m.key
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:text-primary-700")
                }
              >
                <i className={m.icon}></i>
                {m.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 grid gap-4 lg:grid-cols-2">
          {sorted.map((item, idx) => {
            const isTop3 = item.rank <= 3;
            const t = trendIcon[item.trend];
            return (
              <article
                key={item.id}
                className={
                  "group flex cursor-pointer items-center gap-4 rounded-2xl bg-background-50 p-4 transition-all duration-300 hover:-translate-y-1 hover:ring-2 md:p-5 " +
                  (isTop3 ? "ring-2 ring-primary-200 hover:ring-primary-300" : "ring-1 ring-background-200 hover:ring-primary-200")
                }
              >
                <span
                  className={
                    "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl font-heading text-lg font-black md:h-12 md:w-12 " +
                    (isTop3
                      ? "bg-gradient-to-br from-primary-500 to-accent-500 text-background-50"
                      : "bg-background-200 text-foreground-700")
                  }
                >
                  {String(item.rank).padStart(2, "0")}
                </span>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={"whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold " + (toneBg[item.tag] || "bg-background-200 text-foreground-600")}>
                      {item.tag}
                    </span>
                    <span className={"flex items-center gap-0.5 text-xs font-bold " + t.cls}>
                      <i className={t.icon}></i>
                      {item.trend === "new" ? "新" : item.trend === "up" ? "升" : "降"}
                    </span>
                    <span className="ml-auto hidden items-center gap-2 text-xs text-foreground-500 sm:flex">
                      <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{item.hot}</span>
                      <span className="flex items-center gap-1"><i className="ri-chat-1-line"></i>{item.replies}</span>
                    </span>
                  </div>
                  <h3 className="mt-2 truncate text-sm font-bold text-foreground-950 transition-colors group-hover:text-primary-700 md:text-base">
                    {item.title}
                  </h3>
                  <p className="mt-1 text-xs text-foreground-500">第 {idx + 1} 位 · 热度持续上涨中</p>
                </div>

                <a
                  href="#plaza"
                  aria-label={"查看话题：" + item.title}
                  className="hidden h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full bg-background-200 text-foreground-600 transition-colors hover:bg-primary-500 hover:text-background-50 md:flex"
                >
                  <i className="ri-arrow-right-line"></i>
                </a>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}